<template>
  <div class="flex w-full h-full mx-auto p-6">
    <div
      class="bg-white shadow-lg rounded-lg w-2/6 mx-4 pt-4 pb-48 px-4 overflow-hidden"
      style="padding-bottom: 15rem"
    >
      <div class="flex items-center pb-4">
        <h3
          class="flex items-center font-bold text-gray-900 px-2 py-2 leading-tight tracking-wider"
        >
          Lista de alertas
          <span
            class="rounded-full ml-2 border-solid border-2 border-gray-300 px-3 py-1 bg-gray-100"
          >{{filteredAlerts.length}}</span>
        </h3>
        <button class="border-2 rounded border-gray-700" @click="changeAlertOrder">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 fill-current" viewBox="0 0 24 24">
            <path d="M16 17.01V10h-2v7.01h-3L15 21l4-3.99h-3zM9 3L5 6.99h3V14h2V6.99h3L9 3z" />
            <path d="M0 0h24v24H0z" fill="none" />
          </svg>
        </button>
      </div>
      <input
        type="text"
        v-model="search"
        placeholder="Búsqueda de alertas"
        class="w-full appearance-none border rounded py-2 px-3 mb-3 text-gray-900 bg-gray-200 focus:outline-none focus:bg-white focus:border-teal-500"
      />
      <div class="w-full flex">
        <button
          v-on:click="statusFilterAlerts = 'all', filteredAlerts"
          class="w-1/2 hover:bg-cdmx-blue-light font-semibold hover:text-white py-2 px-4 border-t border-l border-b border-cdmx-blue-dark hover:border-transparent rounded"
          v-bind:class="[statusFilterAlerts === 'all' ? 'border-transparent bg-cdmx-blue-light text-white' : 'bg-transparent text-cdmx-blue-dark']"
        >Todos {{alerts.length}}</button>
        <button
          v-on:click="statusFilterAlerts = 'open', filteredAlerts"
          class="w-1/2 hover:bg-cdmx-blue-light font-semibold hover:text-white py-2 px-4 border border-cdmx-blue-dark hover:border-transparent rounded"
          v-bind:class="[statusFilterAlerts === 'open' ? 'border-transparent bg-cdmx-blue-light text-white' : 'bg-transparent text-cdmx-blue-dark']"
        >Abiertas {{filteredAlertsByOpen.length}}</button>
      </div>
      <div class="w-full flex pb-2">
        <button
          v-on:click="statusFilterAlerts = 'assigned', filteredAlerts"
          class="w-1/2 hover:bg-cdmx-blue-light font-semibold hover:text-white py-2 px-4 border-l border-r border-b border-cdmx-blue-dark hover:border-transparent rounded"
          v-bind:class="[statusFilterAlerts === 'assigned' ? 'border-transparent bg-cdmx-blue-light text-white' : 'bg-transparent text-cdmx-blue-dark']"
        >Asignadas {{filteredAlertsByAssigned.length}}</button>
        <button
          v-on:click="statusFilterAlerts = 'attended', filteredAlerts"
          class="w-1/2 hover:bg-cdmx-blue-light font-semibold hover:text-white py-2 px-4 border-r border-b border-cdmx-blue-dark hover:border-transparent rounded"
          v-bind:class="[statusFilterAlerts === 'attended' ? 'border-transparent bg-cdmx-blue-light text-white' : 'bg-transparent text-cdmx-blue-dark']"
        >Atendidas {{filteredAlertsByAttended.length}}</button>
      </div>
      <div class="h-full overflow-auto">
        <div
          v-for="alert in filteredAlerts"
          :key="alert.id"
          class="group flex items-center cursor-pointer my-1 hover:bg-gray-300 py-2 px-5 text-left"
          :class="{'bg-cdmx-blue-light': alert.id === currentAlert.id}"
          @click="selectAlert(alert)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            :class="[alert.id === currentAlert.id ? 'text-gray-100' : 'text-gray-900']"
            class="h-10 w-10 fill-current group-hover:text-gray-900 text-gray-100"
            viewBox="0 0 24 24"
          >
            <path d="M0 0h24v24H0z" fill="none" />
            <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
          </svg>
          <div class="flex flex-col ml-2">
            <span
              :class="[alert.id === currentAlert.id ? 'text-gray-100' : 'text-gray-900']"
              class="group-hover:text-gray-900"
            >{{ alert.fecha_alerta | moment('DD/MM/YYYY hh:mm A')}}</span>
            <span
              :class="[alert.id === currentAlert.id ? 'text-gray-300' : 'text-gray-700']"
              class="text-sm group-hover:text-gray-700"
            >F-{{ alert.id }} {{ alert.status }}</span>
            <span
              :class="[alert.id === currentAlert.id ? 'text-gray-300' : 'text-gray-700']"
              class="text-xs group-hover:text-gray-700"
            >{{ alert.alerta_type }}</span>
          </div>
        </div>
      </div>
    </div>
    <div class="flex flex-wrap bg-white shadow-lg rounded-lg w-4/6 mx-4 py-8 overflow-hidden">
      <div class="w-full px-4 py-2 h-full overflow-auto">
        <div class="w-full">
          <div class="flex">
            <div class="h-80 w-full">
              <l-map
                ref="map"
                class="h-full w-full z-0"
                :zoom="zoom"
                :minZoom="11"
                :center="center"
                :options="{zoomControl: false}"
                @ready="onMapReady"
              >
                <l-geo-json :geojson="polygon" :options-style="geoStyle" />
                <l-marker :lat-lng="center" :visible="showMarker"></l-marker>
              </l-map>
            </div>
          </div>
          <div class="flex w-full">
            <div
              class="flex flex-col w-8/12 px-3 py-2 mx-auto my-3 bg-gray-400 rounded-lg shadow-xl"
            >
              <div class="flex items-center">
                <div class="flex items-center w-1/2 text-left text-xs">
                  <div
                    v-if="currentAlert.status === 'ABIERTA'"
                    class="px-3 py-1 rounded-full border-2 border-red-700 text-white bg-red-500 tracking-widest font-bold"
                  >F-{{currentAlert.id}} {{ currentAlert.status }}</div>
                  <div
                    v-if="currentAlert.status === 'ASIGNADA'"
                    class="px-3 py-1 rounded-full border-2 border-orange-700 text-white bg-orange-500 tracking-widest font-bold"
                  >F-{{currentAlert.id}} {{ currentAlert.status }}</div>
                  <div
                    v-if="currentAlert.status === 'ATENDIDA'"
                    class="px-3 py-1 rounded-full border-2 border-green-700 text-white bg-green-500 tracking-widest font-bold"
                  >F-{{currentAlert.id}} {{ currentAlert.status }}</div>
                  <svg
                    class="ml-4 cursor-pointer"
                    @click="showAlertChatModal(currentAlert)"
                    xmlns="http://www.w3.org/2000/svg"
                    height="24"
                    viewBox="0 0 24 24"
                    width="24"
                  >
                    <path d="M0 0h24v24H0z" fill="none" />
                    <path
                      d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"
                    />
                  </svg>
                </div>
                <div
                  class="w-1/2 text-right text-xs text-gray-900"
                >{{ currentAlert.fecha_alerta | moment('DD/MM/YYYY hh:mm a') }}</div>
              </div>
              <div class="w-full text-xs pt-2 tracking-wide">
                <span class="font-bold">Nombre del visitante:</span>
                {{ currentAlert.user_info.name }} {{ currentAlert.user_info.last_name }}
              </div>
              <div
                class="w-full text-xs pt-2 tracking-wide"
                v-if="currentAlert.autoridad_id !== null"
              >
                <span class="font-bold" v-if="currentAlert.status === 'ASIGNADA'">Asignada a:</span>
                <span class="font-bold" v-if="currentAlert.status === 'ATENDIDA'">Atendida por:</span>
                {{ currentAlert.authority_name }} {{ currentAlert.authority_lastname }}
              </div>
              <p
                class="pb-1 pt-2 text-sm text-left font-bold tracking-widest"
              >Mensaje del visitante:</p>
              <div
                class="w-full px-3 py-2 bg-gray-100 rounded-lg mb-2 text-left text-sm"
              >{{ currentAlert.comentario_visitante }}</div>
              <p class="pb-1 text-sm text-left font-bold tracking-widest">Reporte de autoridad:</p>
              <div
                class="w-full px-3 py-2 bg-gray-100 rounded-lg mb-2 text-left text-sm"
              >{{ currentAlert.comentario_autoridad }}</div>
            </div>
          </div>
        </div>
        <button
          v-if="currentAlert.status === 'ABIERTA'"
          v-on:click="updateDataModal()"
            class="bg-transparent hover:bg-cdmx-blue-light text-cdmx-blue-dark font-semibold hover:text-white py-2 px-4 mx-1 mt-2 border border-cdmx-blue-dark hover:border-transparent rounded"
          >Asignar</button> 
      </div>
    </div>
    <authority-chat-modal
      :showing="showChatModal"
      @close="closeChatsModal"
      :chartList="chartList"
      :alert="chatAlert"
    ></authority-chat-modal>
    <alert-assign-modal
      :showing="showAsignModal"
      @updateAllAlerts="updateAllAlerts"
      @close="showAsignModal = false"
    ></alert-assign-modal> 

    <alert-dialog :showing="showingError">
      <div class="flex items-center justify-center">
        <div class="uppercase text-gray-900">Ha ocurrido un error</div>
        <div class="ml-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="w-8 lg:w-12 h-8 lg:h-12 fill-current text-cdmx-red"
            viewBox="0 0 24 24"
          >
            <path
              d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
            />
            <path d="M0 0h24v24H0z" fill="none" />
          </svg>
        </div>
      </div>
      <div class="w-full">
        <button
          class="mt-4 px-4 py-2 rounded-lg text-gray-900 border-gray-500 border-4 font-bold tracking-wider bg-white hover:bg-gray-300"
          @click="showingError = false"
        >Cerrar</button>
      </div>
    </alert-dialog>
  </div>
</template>

<script>
import { backend_url } from "../config/url.config.ts";
import moment from "moment";
import AlertDialog from "./AlertDialog";
import polygon from "../assets/polygonChapp.json";
import L from "leaflet";
import { LMap, LMarker, LGeoJson } from "vue2-leaflet";
import * as esri from "esri-leaflet";
import { Icon } from "leaflet";
import EventBus from "../event-bus";
import AuthorityChatModal from "./AuthorityChatModal";
import AlertAssignModal from "./AlertAssignModal";

export default {
  name: "alert-list",
  data() {
    return {
      alerts: [],
      currentAlert: {
        user_info: {}
      },
      search: "",
      alertOrder: "downward",
      statusFilterAlerts: "all",
      config: {
        headers: {
          "x-access-token": localStorage.getItem("accessToken")
        }
      },
      showingError: false,
      polygon: polygon,
      zoom: 14,
      center: [19.412257, -99.200871],
      showMarker: false,
      geoStyle: {
        color: "#cb2833",
        fill: false
      },
      chatAlert: null,
      chartList: null,
      showChatModal: false,
      showAsignModal: false
    };
  },
  components: {
    LMap,
    LMarker,
    LGeoJson,
    AlertDialog,
    AlertAssignModal,
    AuthorityChatModal,
  },
  mounted() {
    this.fetchAlerts();
  },
  methods: {
    onMapReady: function() {
      this.$nextTick(() => {
        const map = this.$refs.map.mapObject;
        esri.basemapLayer("Topographic").addTo(map);
      });
    },
    fetchAlerts() {
      this.axios
        .get(backend_url + "/api/alerts/", {}, this.config)
        .then(response => {
          this.alerts = response.data;
          this.currentAlert = this.filteredAlerts[0];
          if (
            this.currentAlert.lat !== null &&
            this.currentAlert.lon !== null
          ) {
            this.center = [this.currentAlert.lat, this.currentAlert.lon];
            this.showMarker = true;
          } else {
            this.showMarker = false;
          }
        })
        .catch(error => {
          console.log(error);
          if (error.response.status == 400 || error.response.status == 401) {
            if (
              error.response.data.name === "TokenExpiredError" ||
              error.response.data.name === "InvalidTokenError" ||
              error.response.data.name === "JsonWebTokenError"
            ) {
              this.$router.push({ path: "/login" });
            }
          }
          this.showingError = false;
        });
    },
    changeAlertOrder() {
      if (this.alertOrder === "upward") {
        this.alertOrder = "downward";
      } else {
        this.alertOrder = "upward";
      }
    },
    selectAlert(alert) {
      this.currentAlert = alert;
      if (alert.lat !== null && alert.lon !== null) {
        this.center = [alert.lat, alert.lon];
        this.showMarker = true;
      } else {
        this.showMarker = false;
      }
    },
    showAlertChatModal(alert) {
      this.axios
        .get(backend_url + "/api/messages/" + alert.id, this.config)
        .then(response => {
          this.chartList = response.data.messages;
          this.chatAlert = alert;
          this.showChatModal = true;
        })
        .catch(error => {
          if (error.response.status == 400 || error.response.status == 401) {
            if (
              error.response.data.name === "TokenExpiredError" ||
              error.response.data.name === "InvalidTokenError" ||
              error.response.data.name === "JsonWebTokenError"
            ) {
              this.$router.push({ path: "/login" });
            }
          }
        });
    },
    closeChatsModal() {
      this.showChatModal = false;
    },

    updateDataModal: function() {
      this.showAsignModal = true;
      EventBus.$emit("asignModal", this.currentAlert);
    },

    updateAllAlerts: function() {
      this.fetchAlerts();
    }
  },
  computed: {
    filteredAlerts() {
      return this.alerts
        .filter(alert => {
          let date = moment(alert.fecha_alerta).format("DD/MM/YYYY hh:mm A");
          switch (this.statusFilterAlerts) {
            case "all":
              return (
                date.toLowerCase().includes(this.search.toLowerCase()) ||
                alert.alerta_type
                  .toLowerCase()
                  .includes(this.search.toLowerCase())
              );
            case "open":
              return (
                (date.toLowerCase().includes(this.search.toLowerCase()) ||
                  alert.alerta_type
                    .toLowerCase()
                    .includes(this.search.toLowerCase())) &&
                alert.status == "ABIERTA"
              );
            case "assigned":
              return (
                (date.toLowerCase().includes(this.search.toLowerCase()) ||
                  alert.alerta_type
                    .toLowerCase()
                    .includes(this.search.toLowerCase())) &&
                alert.status == "ASIGNADA"
              );
            case "attended":
              return (
                (date.toLowerCase().includes(this.search.toLowerCase()) ||
                  alert.alerta_type
                    .toLowerCase()
                    .includes(this.search.toLowerCase())) &&
                alert.status == "ATENDIDA"
              );
            default:
              break;
          }
        })
        .sort((a, b) => {
          if (this.alertOrder === "upward") {
            return new Date(a.fecha_alerta) - new Date(b.fecha_alerta);
          } else {
            return new Date(b.fecha_alerta) - new Date(a.fecha_alerta);
          }
        });
    },
    filteredAlertsByOpen() {
      return this.alerts.filter(alert => {
        return alert.status == "ABIERTA";
      });
    },
    filteredAlertsByAssigned() {
      return this.alerts.filter(alert => {
        return alert.status == "ASIGNADA";
      });
    },
    filteredAlertsByAttended() {
      return this.alerts.filter(alert => {
        return alert.status == "ATENDIDA";
      });
    }
  }
};
</script>

<style scoped>
</style>